import CryptoJS from 'crypto-js';

/**
 * Validates if a key meets requirements
 * @param {string} key - The encryption key to validate
 * @returns {Object} - Contains validation result and error message if any
 */
export const validateKey = (key) => {
  if (!key || key.length < 4) {
    return {
      isValid: false,
      error: 'The key must be at least 4 characters long',
    };
  }
  return { isValid: true, error: '' };
};

/**
 * Encrypts file data using AES encryption
 * @param {ArrayBuffer} fileData - The file data to encrypt
 * @param {string} key - The encryption key
 * @returns {Promise<Object>} - Contains encrypted string or error
 */
export const encryptFile = async (fileData, key) => {
  try {
    // Validate the key
    const keyValidation = validateKey(key);
    if (!keyValidation.isValid) {
      return { success: false, error: keyValidation.error };
    }

    // Convert the ArrayBuffer to a WordArray
    const wordArray = CryptoJS.lib.WordArray.create(fileData);

    // Encrypt the WordArray
    const encrypted = CryptoJS.AES.encrypt(wordArray, key);
    if (!encrypted) {
      throw new Error('Encryption failed');
    }

    return {
      success: true,
      data: encrypted.toString(),
    };
  } catch (error) {
    console.error('Encryption error:', error);
    return {
      success: false,
      error: error.message || 'An error occurred during encryption',
    };
  }
};

/**
 * Decrypts encrypted data using AES decryption
 * @param {string} encryptedData - The encrypted string
 * @param {string} key - The decryption key
 * @param {string} fileFormat - The format of the original file
 * @returns {Promise<Object>} - Contains decrypted data URL or error
 */
export const decryptFile = async (encryptedData, key, fileFormat) => {
  try {
    // Validate the key
    const keyValidation = validateKey(key);
    if (!keyValidation.isValid) {
      return { success: false, error: keyValidation.error };
    }

    if (!encryptedData) {
      return { success: false, error: 'No encrypted data provided' };
    }

    // Decrypt the data
    const decrypted = CryptoJS.AES.decrypt(encryptedData, key);
    const decryptedBytes = decrypted.toString(CryptoJS.enc.Latin1);

    // Convert to Uint8Array
    const bytes = new Uint8Array(decryptedBytes.length);
    for (let i = 0; i < decryptedBytes.length; i++) {
      bytes[i] = decryptedBytes.charCodeAt(i);
    }

    // Create a Blob from decrypted data
    const blob = new Blob([bytes], {
      type: `image/${fileFormat.toLowerCase()}`,
    });

    return {
      success: true,
      data: URL.createObjectURL(blob),
      blob: blob,
    };
  } catch (error) {
    console.error('Decryption error:', error);
    return {
      success: false,
      error: 'Decryption failed. Check your key or encrypted data.',
    };
  }
};

/**
 * Generate a timestamp-based filename
 * @param {string} prefix - Prefix for the filename
 * @param {string} extension - File extension
 * @returns {string} - Generated filename
 */
export const generateFilename = (prefix, extension) => {
  const now = new Date();
  const formattedDate = now
    .toISOString()
    .replace(/[-:]/g, '')
    .replace('T', '_')
    .split('.')[0];
  
  return `${prefix}_${formattedDate}.${extension.toLowerCase()}`;
};

/**
 * Validates file type and size
 * @param {File} file - The file to validate
 * @returns {Object} - Contains validation result and error message if any
 */
export const validateFile = (file) => {
  if (!file) {
    return { isValid: false, error: 'No file selected' };
  }

  // Get allowed file types from env
  const allowedTypes = process.env.REACT_APP_FILE_TYPES 
    ? process.env.REACT_APP_FILE_TYPES.split(',') 
    : [
        'image/png',
        'image/jpeg',
        'image/jpg',
        'image/gif',
        'application/pdf',
        'text/plain'
      ];
  
  // Get max file size from env or default to 100MB
  const maxSize = process.env.REACT_APP_MAX_FILE_SIZE 
    ? parseInt(process.env.REACT_APP_MAX_FILE_SIZE, 10)
    : 100 * 1024 * 1024;

  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: `File type not supported. Allowed types: ${allowedTypes.join(', ')}`,
    };
  }

  if (file.size > maxSize) {
    return {
      isValid: false,
      error: `File size exceeds the limit of ${Math.floor(maxSize / (1024 * 1024))}MB`,
    };
  }

  return { isValid: true, error: '' };
};